import type { ReactNode } from 'react';

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME ?? 'VoxFaunix';

interface Section {
  key: string;
  heading: string;
  items: { q: string; a: ReactNode }[];
}

/**
 * How it works, and every question a contributor is likely to actually ask —
 * answered from what the product actually does, not from marketing copy.
 *
 * Static and unauthenticated-safe by design: nothing here needs the API, so
 * it renders instantly and works even if a fetch elsewhere on the page is
 * still loading or has failed. Grouped with native <details>/<summary>
 * rather than a JS accordion — free keyboard support, free find-in-page,
 * nothing to hydrate.
 */
export function HelpPage() {
  return (
    <div>
      <p className="eyebrow">Help</p>
      <h1 className="page-title">How {BRAND_NAME} works</h1>
      <p className="page-intro">
        {BRAND_NAME} is a corpus of context-labelled animal communication — recordings paired with
        what triggered them and what happened next. Everything below is how that actually works,
        answered plainly.
      </p>

      {SECTIONS.map((section) => (
        <section className="panel help-section" key={section.key}>
          <p className="eyebrow" style={{ marginBottom: '0.9rem' }}>
            {section.heading}
          </p>
          <dl className="help-list">
            {section.items.map((item, i) => (
              <details className="help-item" key={i}>
                <summary className="help-question">{item.q}</summary>
                <div className="help-answer">{item.a}</div>
              </details>
            ))}
          </dl>
        </section>
      ))}

      <section className="panel help-section help-contact">
        <p>
          <span className="eyebrow" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Still stuck?
          </span>
          <span className="field-hint" style={{ fontSize: '0.95rem', margin: 0 }}>
            A bug, a question, or just an opinion — every message is read, even the short ones.
          </span>
        </p>
        <a className="btn" href="#/contact">
          Contact us
        </a>
      </section>
    </div>
  );
}

const SECTIONS: Section[] = [
  {
    key: 'basics',
    heading: 'The basics',
    items: [
      {
        q: 'What am I actually building by using this?',
        a: (
          <>
            An open, reusable corpus of animal communication. Most audio datasets of animal sounds
            are just sounds — a bark on its own tells you almost nothing about what it meant. Every{' '}
            {BRAND_NAME} recording is required to carry three things together: the signal itself,{' '}
            <strong>what happened right before it</strong> (the antecedent), and{' '}
            <strong>what happened right after</strong> (the consequence). That structure — called
            A–B–C — is what turns "a dog barked" into evidence about <em>why</em>.
          </>
        ),
      },
      {
        q: 'Why does it insist on the "before" and "after", not just the recording?',
        a: (
          <>
            Because a signal without context is nearly unusable for understanding function. A bark
            triggered by a knock at the door and a bark triggered by another dog approaching are the
            same sound serving completely different purposes. Researchers, trainers and behaviourists
            need the trigger and the outcome to say anything meaningful — so the app asks for both, one
            question at a time, before it will accept a submission.
          </>
        ),
      },
      {
        q: 'Who is this for — do I need any expertise?',
        a: 'No. It is built for anyone who spends time around an animal — a pet owner, a keeper, a farmer, a field researcher. The A–B–C questions offer a list of common terms plus free text, so you describe what you actually observed in your own words if nothing on the list fits.',
      },
      {
        q: 'Does it work for any animal?',
        a: 'Any species can be recorded, but the list of signal/context terms offered (the "ethogram") is specific to the species you pick, and needs to have been published for that species first. If your animal is not yet supported, you can still record with free-text descriptions.',
      },
    ],
  },
  {
    key: 'recording',
    heading: 'Recording and submitting',
    items: [
      {
        q: "What are 'Animals' and why do I register mine before recording?",
        a: (
          <>
            An "animal" (called a <em>subject</em> in the data) is the individual you are recording —
            not a species. Registering your own animal once, then recording against it repeatedly, is
            what makes your Timeline possible and is also scientifically important: repeated recordings
            of the <em>same</em> individual can be analysed as repeated measures of one animal, not as
            unrelated data points from strangers. Most casual datasets lose this distinction and can
            never get it back.
          </>
        ),
      },
      {
        q: 'What happens during the three questions (antecedent, signal, consequence)?',
        a: (
          <>
            <strong>Antecedent</strong> — what was happening right before the animal made the sound
            (a trigger, an approach, nothing you noticed). <strong>Signal</strong> — the recording
            itself, plus what kind of sound/behaviour it was. <strong>Consequence</strong> — what the
            animal did afterwards, and whether the situation resolved. Pick terms from the list where
            they fit, or describe it in your own words — both are accepted.
          </>
        ),
      },
      {
        q: 'Why was my recording rejected?',
        a: (
          <>
            Every submission passes a set of checks before it is accepted, and a rejection always
            lists every rule it failed, not just the first one. The common ones: the audio's checksum
            didn't match what storage actually received (a corrupted or interrupted upload — try
            again), a required consent or welfare confirmation wasn't given, or a chosen term isn't
            part of the current ethogram for that species (vocabularies are corrected periodically —
            reload and try again). Recordings where the animal was deliberately provoked or distressed
            are always rejected; that one is not configurable, and it will not change.
          </>
        ),
      },
      {
        q: 'Can I fix or delete a recording after submitting it?',
        a: (
          <>
            Recordings are permanent once accepted — there is no edit or delete. A correction creates
            a <em>new</em> record that explicitly supersedes the old one; both stay retrievable. This
            is deliberate: it is what lets a snapshot of the corpus be cited (with a stable DOI) without
            the ground shifting under a researcher who already used it. If a recording is genuinely
            wrong,{' '}
            <a href="#/contact" style={{ color: 'var(--context-ink)' }}>
              contact us
            </a>{' '}
            — a withdrawal is possible even though an edit is not.
          </>
        ),
      },
      {
        q: 'What audio format should I use?',
        a: 'Record directly in the app when you can — it captures a live level meter and a spectrogram so you can see clipping before you submit. Lossless formats (WAV/FLAC) are preferred over lossy (MP3/AAC) where you have a choice; the app tells you which one your upload counted as.',
      },
    ],
  },
  {
    key: 'credits',
    heading: 'Field credits, stamps and your Timeline',
    items: [
      {
        q: 'What are Field credits?',
        a: (
          <>
            A running score of what your listening has added to the corpus so far — shown as a badge
            in the header on every page, and broken down in full on the{' '}
            <a href="#/record" style={{ color: 'var(--context-ink)' }}>
              Field record
            </a>{' '}
            page. Every accepted recording earns credits, and a fully-coded A–B–C (all three parts
            filled in, not just the recording) earns a bonus on top. The breakdown on that page shows
            exactly which recordings earned what — the number is never a black box.
          </>
        ),
      },
      {
        q: 'What do credits redeem for?',
        a: 'Nothing yet, deliberately. What they eventually redeem for is still being decided together with the first contributors, and the app would rather promise nothing than promise something it might have to walk back. Credits accumulate from the day you start and never expire.',
      },
      {
        q: 'What are the stamps on the Field record page?',
        a: 'Milestones for how much you have recorded (your first, then 10, 25 and 100) plus one for care — ten recordings with a complete A–B–C. Two more categories (clean audio, rare calls) are part of the design but need a server-side pass the app cannot yet do honestly, so they stay hidden rather than shown as permanently unearned.',
      },
      {
        q: 'What is the Timeline for?',
        a: "Your own recordings, organised — what you recorded, what triggered it, what happened after. It answers a different question than Field record does: Timeline is about what you're learning about your animal; Field record is about what your listening has built. They're kept on separate pages on purpose so an animal's own page stays about the animal, not about you.",
      },
    ],
  },
  {
    key: 'privacy',
    heading: 'Privacy, consent and the corpus licence',
    items: [
      {
        q: 'Is my location shared exactly as I gave it?',
        a: "No. Coordinates are snapped to a coarse grid before they're ever stored — precision that was never kept can't later leak. Exact locations are never published.",
      },
      {
        q: "Does the corpus know who I am, or is it anonymous?",
        a: (
          <>
            The corpus never carries your email, name or sign-in identity. A separate, unrelated
            identifier is generated for you the first time you sign in, and only that identifier
            appears in your recordings — there's no way to work backwards from a published record to
            who submitted it.
          </>
        ),
      },
      {
        q: "What am I agreeing to when I accept the contributor terms?",
        a: 'A licence to include your recordings in the published corpus, so researchers and organisations can use it. You keep the right to be credited as a contributor; you are not signing away ownership of your recordings, and the corpus is never sold outright to a single buyer — see the terms shown before you accept for the exact wording, since that is what actually governs.',
      },
      {
        q: 'Can other people or companies use the corpus?',
        a: "The corpus is openly licensed for research use. Commercial use is possible but requires a separate conversation with us first — that's what funds keeping the whole thing free and ad-free for contributors, rather than the corpus itself being sold with no benefit flowing back.",
      },
    ],
  },
  {
    key: 'account',
    heading: 'Your account',
    items: [
      {
        q: 'Why is sign-in just an emailed code, no password?',
        a: "There's nothing to remember and nothing for us to leak. Each sign-in emails a short-lived one-time code; entering it correctly signs you in. There is no separate sign-up step — the first code you verify sets your account up.",
      },
      {
        q: "I signed out and back in and my animals/recordings are gone. What happened?",
        a: (
          <>
            That's a sign-in problem, not a data-loss one — it means the sign-in produced a different
            account than last time. Recordings and animals are never deleted on sign-out. If this
            happens to you,{' '}
            <a href="#/contact" style={{ color: 'var(--context-ink)' }}>
              contact support
            </a>{' '}
            with the email you used both times; do not re-enter your animals until it's confirmed
            which account is the right one, to avoid duplicates.
          </>
        ),
      },
      {
        q: 'Can I use one account across devices?',
        a: 'Yes — sign in with the same email on each device and you land on the same account, same Timeline, same credits.',
      },
    ],
  },
];
